import { QueryKey, QueryObserverResult } from '@tanstack/query-core'
import { WithInitialValue } from 'components/widgets/invest-component/interfaces/common'
import { Atom, atom, Getter, PrimitiveAtom } from 'jotai'
import { atomsWithQuery } from 'jotai-tanstack-query'
import { loadable } from 'jotai/utils'

type Atoms<T, U, K> = {
  first?: PrimitiveAtom<T> & WithInitialValue<T>
  second?: PrimitiveAtom<U> & WithInitialValue<U>
  third?: PrimitiveAtom<K> & WithInitialValue<K>
}

type Response<T> = {
  response: Atom<T | Promise<T>>
  dispatch: Atom<QueryObserverResult<T, unknown>>
}

//INFO: Function to return queryKey properties
function getKey<A1, A2, A3, K>(
  atoms: Atoms<A1, A2, A3>,
  keyFn: (
    a1?: A1,
    a2?: A2,
    a3?: A3,
  ) => {
    foreground: K
    background: K
  },
  get: Getter,
): {
  foreground: K
  background: K
} {
  return keyFn(
    atoms.first ? get(atoms.first) : undefined,
    atoms.second ? get(atoms.second) : undefined,
    atoms.third ? get(atoms.third) : undefined,
  )
}

//INFO: Function to create atoms with query functionality
export const createAtomsWithQuery = <
  T,
  A1 = null,
  A2 = null,
  A3 = null,
  GKey extends QueryKey = QueryKey,
>(
  atoms: Atoms<A1, A2, A3>,
  key: (
    a1?: A1,
    a2?: A2,
    a3?: A3,
  ) => {
    foreground: GKey
    background: GKey
  },
  func: ({ queryKey }: { queryKey: GKey }) => Promise<T>,
  interval?: number,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  functionName?: string,
  staleTime?: number,
): Response<T> => {
  const foreground = atomsWithQuery<unknown, unknown, T, unknown, GKey>((get) => {
    const { foreground } = getKey<A1, A2, A3, GKey>(atoms, key, get)
    // INFO: for testing
    // if (errorKeys.includes(foreground[0] as string)) {
    //   throw new Error(`Fails in ${functionName} function with a key ${foreground[0]}`)
    // }

    return {
      queryKey: foreground,
      queryFn: func,
      throwOnError: true,
      retry: 2,
    }
  })

  const background = atomsWithQuery<unknown, unknown, T, unknown, GKey>((get) => {
    const { background } = getKey<A1, A2, A3, GKey>(atoms, key, get)
    // INFO: for testing
    // if (errorKeys.includes(background[0] as string)) {
    //   throw new Error(`Fails in ${functionName} function with a key ${background[0]}`)
    // }
    return {
      queryKey: background,
      queryFn: func,
      refetchInterval: interval,
      staleTime: staleTime ?? Infinity,
      throwOnError: false,
    }
  })

  const [foregroundResponse, foregroundDispatch] = foreground
  const [backgroundResponse, backgroundDispatch] = background

  const foregroundQueryAtom = loadable(foregroundResponse)
  const backgroundQueryAtom = loadable(backgroundResponse)

  const isBackgroundActiveAtom = atom((get) => get(foregroundQueryAtom).state === 'hasData')
  const isBackgroundDataValidAtom = atom((get) => get(backgroundQueryAtom).state === 'hasData')

  const responseAtom = atom((get) =>
    !get(isBackgroundActiveAtom) || !get(isBackgroundDataValidAtom)
      ? get(foregroundResponse)
      : get(backgroundResponse),
  )

  const dispatchAtom = atom((get) =>
    !get(isBackgroundActiveAtom) || !get(isBackgroundDataValidAtom)
      ? get(foregroundDispatch)
      : get(backgroundDispatch),
  )

  return {
    response: responseAtom,
    dispatch: dispatchAtom,
  }
}
